import type { Fill, Primitive, Stroke, TextPrimitive } from '@vp/shared-types';
import { keepsDetail } from './display';
import { adaptiveGridStep, fmod, type BackendStats, type FrameInput, type GridSettings, type RenderBackend } from './backend';
import { cssColor, INK_FILTER } from './color';
import { arrowHead, dashPattern, trimForArrows } from './pathgeom';
import { orientTris, roundStroke, strokeVariable } from './webgl/tessellate';
import { fontString, layoutText } from './text';
import type { CameraState } from './camera';
import type { RenderItem } from './scene';

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Canvas 2D fallback backend (§11.2). Also used for PNG export. */
export class Canvas2DBackend implements RenderBackend {
  readonly kind = 'canvas2d' as const;
  readonly lost = false;
  private ctx: Ctx;
  private drawCalls = 0;
  private instances = 0;

  constructor(private readonly canvas: HTMLCanvasElement | OffscreenCanvas) {
    const ctx = canvas.getContext('2d', { alpha: true }) as Ctx | null;
    if (!ctx) throw new Error('Canvas 2D is not available');
    this.ctx = ctx;
  }

  resize(width: number, height: number, dpr: number): void {
    this.canvas.width = Math.max(1, Math.round(width * dpr));
    this.canvas.height = Math.max(1, Math.round(height * dpr));
  }

  stats(): BackendStats {
    return { drawCalls: this.drawCalls, instances: this.instances, textures: 0, textureBytes: 0, pending: 0 };
  }

  dispose(): void {
    /* nothing to release */
  }

  render(frame: FrameInput): void {
    const { ctx } = this;
    const cam = frame.camera;
    this.drawCalls = 0;
    this.instances = 0;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    // dark canvas in the fallback: one CSS filter on the element (images get inverted too here)
    if ('style' in this.canvas) this.canvas.style.filter = frame.darkInk ? INK_FILTER : '';
    ctx.fillStyle = frame.background;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    if (frame.grid?.enabled) drawGrid(ctx, cam, frame.grid);
    const s = cam.zoom * cam.dpr;
    const W2 = (cam.width * cam.dpr) / 2;
    const H2 = (cam.height * cam.dpr) / 2;
    for (const item of frame.items) {
      const clip = item.clip;
      if (clip) {
        // frame clipping in device pixels
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.beginPath();
        ctx.rect((clip.x - cam.cx) * s + W2, (clip.y - cam.cy) * s + H2, clip.w * s, clip.h * s);
        ctx.clip();
      }
      this.drawItem(item, cam, s, W2, H2, frame);
      if (clip) ctx.restore();
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
  }

  private drawItem(item: RenderItem, cam: CameraState, s: number, W2: number, H2: number, frame: FrameInput): void {
    const ctx = this.ctx;
    const o = item.obj;
    // LOD: sub-3px objects become a single filled square (same look as the GPU path at that size)
    if (Math.max(o.w, o.h) * s < 3) {
      const p0 = item.prims[0];
      let color: string | null = null;
      if (p0?.k === 'shape') color = p0.fill.kind !== 'none' ? p0.fill.color : p0.stroke?.color ?? null;
      else if (p0?.k === 'path') color = p0.stroke?.color ?? null;
      else if (p0) color = '#cbd5e1';
      if (color) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = o.style.opacity ?? 1;
        ctx.fillStyle = cssColor(color);
        const x = (o.x - cam.cx) * s + W2;
        const y = (o.y - cam.cy) * s + H2;
        ctx.fillRect(x, y, Math.max(1, o.w * s), Math.max(1, o.h * s));
        this.instances++;
      }
      return;
    }
    // object center relative to camera computed in float64 → small values for the canvas matrix (§4.2)
    const ocx = (o.x + o.w / 2 - cam.cx) * s + (cam.width * cam.dpr) / 2;
    const ocy = (o.y + o.h / 2 - cam.cy) * s + (cam.height * cam.dpr) / 2;
    const c = Math.cos(o.rotation);
    const sn = Math.sin(o.rotation);
    ctx.setTransform(s * c, s * sn, -s * sn, s * c, ocx, ocy);
    ctx.translate(-o.w / 2, -o.h / 2);
    const alpha = o.style.opacity ?? 1;
    for (const p of item.prims) {
      drawPrimitive(ctx, p, alpha, s, frame);
      this.instances++;
    }
    this.drawCalls++;
  }
}

export function drawPrimitive(ctx: Ctx, p: Primitive, alpha: number, scale: number, frame: Pick<FrameInput, 'images' | 'textLodPx'>): void {
  const a = alpha * (p.opacity ?? 1);
  switch (p.k) {
    case 'shape': {
      const path = new Path2D();
      shapePath(path, p.shape, p.x, p.y, p.w, p.h, p.radius ?? 0);
      fillPath(ctx, path, p.fill, a, p.x, p.y, p.w, p.h, scale);
      strokePath(ctx, path, p.stroke, a);
      return;
    }
    case 'path': {
      const sw = p.stroke?.width ?? 1;
      if (p.closed && p.fill.kind !== 'none') {
        const path = new Path2D();
        polyPath(path, p.points, true);
        for (const h of p.holes ?? []) polyPath(path, h, true);
        const b = pointsBounds(p.points);
        fillPath(ctx, path, p.fill, a, b.x, b.y, b.w, b.h, scale, p.holes?.length ? 'evenodd' : 'nonzero');
      }
      if (p.stroke) {
        const prev = ctx.globalCompositeOperation;
        // the dark canvas here is a CSS filter over the light drawing, so darken works for both
        if (p.blend === 'highlight') ctx.globalCompositeOperation = 'darken';
        const pts = p.closed ? p.points : trimForArrows(p.points, !!p.arrowStart, !!p.arrowEnd, sw);
        const vary = p.widths && p.widths.length === p.points.length / 2 && pts === p.points;
        if (vary) {
          const tris: number[] = [];
          strokeVariable(pts, p.widths!, p.closed, tris);
          if (p.round) roundStroke(pts, p.widths!, tris, scale);
          fillTris(ctx, tris, cssColor(p.stroke.color), a);
        } else {
          const path = new Path2D();
          polyPath(path, pts, p.closed);
          if (p.closed) for (const h of p.holes ?? []) polyPath(path, h, true);
          strokePath(ctx, path, p.stroke, a, !!p.round);
        }
        for (const atEnd of [false, true]) {
          if (p.closed || !(atEnd ? p.arrowEnd : p.arrowStart)) continue;
          const head = arrowHead(p.points, atEnd, sw);
          if (!head) continue;
          ctx.beginPath();
          polyPath(ctx, head.tri, true);
          ctx.globalAlpha = a;
          ctx.fillStyle = cssColor(p.stroke.color);
          ctx.fill();
        }
        ctx.globalCompositeOperation = prev;
      }
      return;
    }
    case 'text':
      drawText(ctx, p, a, scale, keepsDetail(p) ? 0 : frame.textLodPx);
      return;
    case 'image': {
      const img = frame.images.get(p.src);
      ctx.globalAlpha = a;
      if (img) ctx.drawImage(img as CanvasImageSource, p.x, p.y, p.w, p.h);
      else {
        ctx.fillStyle = '#e5e7eb';
        ctx.fillRect(p.x, p.y, p.w, p.h);
      }
      return;
    }
    case 'raster': {
      ctx.globalAlpha = a;
      if (p.bitmap) ctx.drawImage(p.bitmap, p.x, p.y, p.w, p.h);
      else {
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(p.x, p.y, p.w, p.h);
      }
      return;
    }
  }
}

export function drawText(ctx: Ctx, p: TextPrimitive, alpha: number, scale: number, lodPx: number): void {
  const layout = layoutText(p.text, p.style, p.w, p.h);
  ctx.globalAlpha = alpha;
  if (p.style.fontSize * scale < lodPx) {
    // greeking: draw lines as faint bars
    ctx.fillStyle = cssColor(p.style.color, 0.35);
    const bh = p.style.fontSize * 0.5;
    for (const l of layout.lines) if (l.width > 0) ctx.fillRect(p.x + l.x, p.y + l.y - bh, l.width, bh);
    return;
  }
  // glyphs at their pixel size: browsers do not draw huge world-size fonts shrunk by a transform
  const k = scale > 0 && Number.isFinite(scale) ? scale : 1;
  ctx.save();
  ctx.scale(1 / k, 1 / k);
  ctx.font = fontString(p.style, p.style.fontSize * k);
  ctx.fillStyle = cssColor(p.style.color);
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  for (const l of layout.lines) if (l.text) ctx.fillText(l.text, (p.x + l.x) * k, (p.y + l.y) * k);
  ctx.restore();
}

export function shapePath(ctx: Ctx | Path2D, shape: string, x: number, y: number, w: number, h: number, radius: number): void {
  switch (shape) {
    case 'rect': {
      const r = Math.min(radius, w / 2, h / 2);
      if (r > 0) {
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
      } else {
        ctx.moveTo(x, y);
        ctx.lineTo(x + w, y);
        ctx.lineTo(x + w, y + h);
        ctx.lineTo(x, y + h);
        ctx.closePath();
      }
      return;
    }
    case 'ellipse':
      ctx.ellipse(x + w / 2, y + h / 2, Math.max(w / 2, 0), Math.max(h / 2, 0), 0, 0, Math.PI * 2);
      ctx.closePath();
      return;
    case 'triangle':
      ctx.moveTo(x + w / 2, y);
      ctx.lineTo(x + w, y + h);
      ctx.lineTo(x, y + h);
      ctx.closePath();
      return;
    case 'diamond':
      ctx.moveTo(x + w / 2, y);
      ctx.lineTo(x + w, y + h / 2);
      ctx.lineTo(x + w / 2, y + h);
      ctx.lineTo(x, y + h / 2);
      ctx.closePath();
      return;
  }
}

function polyPath(ctx: Ctx | Path2D, pts: number[], closed: boolean): void {
  ctx.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
  if (closed) ctx.closePath();
}

function pointsBounds(pts: number[]): { x: number; y: number; w: number; h: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    x0 = Math.min(x0, pts[i]);
    x1 = Math.max(x1, pts[i]);
    y0 = Math.min(y0, pts[i + 1]);
    y1 = Math.max(y1, pts[i + 1]);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Pattern geometry shared with the WebGL shader and SVG export. */
export function patternSpacing(fill: Fill): number {
  return fill.kind === 'lines' || fill.kind === 'grid' ? fill.spacing ?? 10 : 10;
}
export function patternThickness(spacing: number): number {
  return Math.max(spacing * 0.12, 0.5);
}

function fillPath(ctx: Ctx, path: Path2D, fill: Fill, alpha: number, x: number, y: number, w: number, h: number, scale = 1, rule: CanvasFillRule = 'nonzero'): void {
  if (fill.kind === 'none') return;
  ctx.globalAlpha = alpha;
  if (fill.kind === 'solid') {
    ctx.fillStyle = cssColor(fill.color);
    ctx.fill(path, rule);
    return;
  }
  const sp0 = patternSpacing(fill);
  if (sp0 * scale < 4) {
    // too dense to resolve on screen: average tone instead of moire (matches the GPU path)
    const th0 = patternThickness(sp0);
    const density = Math.min(1, fill.kind === 'grid' ? (2 * th0) / sp0 : th0 / sp0);
    if (fill.background) {
      ctx.fillStyle = cssColor(fill.background);
      ctx.fill(path, rule);
    }
    ctx.globalAlpha = alpha * density;
    ctx.fillStyle = cssColor(fill.color);
    ctx.fill(path, rule);
    ctx.globalAlpha = alpha;
    return;
  }
  ctx.save();
  ctx.clip(path, rule);
  if (fill.background) {
    ctx.fillStyle = cssColor(fill.background);
    ctx.fillRect(x, y, w, h);
  }
  const sp = patternSpacing(fill);
  const th = patternThickness(sp);
  ctx.strokeStyle = cssColor(fill.color);
  ctx.lineWidth = th;
  ctx.setLineDash([]);
  ctx.beginPath();
  if (fill.kind === 'lines') {
    const ang = fill.angle ?? Math.PI / 4;
    // lines perpendicular to direction n = (cos, sin); offsets k*sp along n, in local coords
    const nx = Math.cos(ang);
    const ny = Math.sin(ang);
    const corners = [x * nx + y * ny, (x + w) * nx + y * ny, x * nx + (y + h) * ny, (x + w) * nx + (y + h) * ny];
    const dMin = Math.min(...corners);
    const dMax = Math.max(...corners);
    const L = w + h;
    const cx = x + w / 2;
    const cy = y + h / 2;
    let count = 0;
    for (let d = Math.ceil(dMin / sp) * sp; d <= dMax && count < 5000; d += sp, count++) {
      // point on the line closest to box center
      const t = d - (cx * nx + cy * ny);
      const px = cx + nx * t;
      const py = cy + ny * t;
      ctx.moveTo(px - ny * L, py + nx * L);
      ctx.lineTo(px + ny * L, py - nx * L);
    }
  } else {
    let count = 0;
    for (let gx = Math.ceil(x / sp) * sp; gx <= x + w && count < 5000; gx += sp, count++) {
      ctx.moveTo(gx, y);
      ctx.lineTo(gx, y + h);
    }
    for (let gy = Math.ceil(y / sp) * sp; gy <= y + h && count < 10000; gy += sp, count++) {
      ctx.moveTo(x, gy);
      ctx.lineTo(x + w, gy);
    }
  }
  ctx.stroke();
  ctx.restore();
}

function fillTris(ctx: Ctx, tris: number[], color: string, alpha: number): void {
  if (tris.length < 6) return;
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  // one winding for every triangle: overlaps of the nonzero fill stay filled instead of cancelling out
  const t = orientTris(tris);
  for (let i = 0; i < t.length; i += 6) {
    ctx.moveTo(t[i], t[i + 1]);
    ctx.lineTo(t[i + 2], t[i + 3]);
    ctx.lineTo(t[i + 4], t[i + 5]);
    ctx.closePath();
  }
  ctx.fill();
}

function strokePath(ctx: Ctx, path: Path2D, stroke: Stroke | null, alpha: number, round = false): void {
  if (!stroke || stroke.width <= 0) return;
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = cssColor(stroke.color);
  ctx.lineWidth = stroke.width;
  ctx.setLineDash(dashPattern(stroke.dash, stroke.width));
  ctx.lineCap = round || stroke.dash === 'dotted' ? 'round' : 'butt';
  ctx.lineJoin = round ? 'round' : 'miter';
  ctx.miterLimit = 4;
  ctx.stroke(path);
  ctx.setLineDash([]);
}

function drawGrid(ctx: Ctx, cam: CameraState, grid: GridSettings): void {
  const minPx = grid.kind === 'dots' ? 16 : 8;
  const step = adaptiveGridStep(grid.step, cam.zoom, minPx);
  const W = cam.width * cam.dpr;
  const H = cam.height * cam.dpr;
  const s = cam.zoom * cam.dpr;
  const stepPx = step * s;
  // screen position of the first grid line: offset computed via float64 modulo
  const left = cam.cx - cam.width / 2 / cam.zoom;
  const top = cam.cy - cam.height / 2 / cam.zoom;
  const ox = (step - fmod(left, step)) % step;
  const oy = (step - fmod(top, step)) % step;
  const major = step * 5;
  const mxStart = Math.round(fmod(left + ox, major) / step); // index of first line inside major cell
  const myStart = Math.round(fmod(top + oy, major) / step);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  if (grid.kind === 'dots') {
    ctx.fillStyle = cssColor(grid.color);
    const r = Math.max(1, Math.min(2, stepPx / 12)) * cam.dpr;
    ctx.beginPath();
    for (let x = ox * s; x <= W; x += stepPx) for (let y = oy * s; y <= H; y += stepPx) ctx.rect(x - r / 2, y - r / 2, r, r);
    ctx.fill();
    return;
  }
  const drawLines = (strong: boolean) => {
    ctx.beginPath();
    let i = 0;
    for (let x = ox * s; x <= W; x += stepPx, i++) {
      const isMajor = (mxStart + i) % 5 === 0;
      if (grid.kind === 'cells' && isMajor !== strong) continue;
      if (grid.kind === 'lines' && strong) continue;
      const px = Math.round(x) + 0.5;
      ctx.moveTo(px, 0);
      ctx.lineTo(px, H);
    }
    i = 0;
    for (let y = oy * s; y <= H; y += stepPx, i++) {
      const isMajor = (myStart + i) % 5 === 0;
      if (grid.kind === 'cells' && isMajor !== strong) continue;
      if (grid.kind === 'lines' && strong) continue;
      const py = Math.round(y) + 0.5;
      ctx.moveTo(0, py);
      ctx.lineTo(W, py);
    }
    ctx.strokeStyle = cssColor(grid.color, strong ? 1 : grid.kind === 'cells' ? 0.45 : 0.7);
    ctx.lineWidth = 1;
    ctx.stroke();
  };
  drawLines(false);
  if (grid.kind === 'cells') drawLines(true);
}
